import type { LscArchiveItem, LscMatch } from './types';

export const NAV_LABEL_HOME = 'League home';
export const NAV_LABEL_COMMAND_CENTER = 'Command Center';
export const NAV_LABEL_LEAGUE_SETUP = 'League Setup';
export const NAV_LABEL_IMPORT_SYNC = 'Import/Sync';
export const NAV_LABEL_LEAGUE_SCHEDULES = 'Games';
export const NAV_LABEL_TOTAL_GAMES = 'Total Games';
export const NAV_LABEL_RTTS = 'Studio';

export const COMMISSIONER_NAV_LABELS = [
  NAV_LABEL_COMMAND_CENTER,
  NAV_LABEL_HOME,
  NAV_LABEL_LEAGUE_SETUP,
  'Members',
  NAV_LABEL_IMPORT_SYNC,
  NAV_LABEL_LEAGUE_SCHEDULES,
  'Needs Attention',
  'VOD Sync',
  'Archive',
  'Settings',
] as const;

export const MEMBER_NAV_LABELS = [
  NAV_LABEL_HOME,
  NAV_LABEL_LEAGUE_SCHEDULES,
  'Archive',
  'Settings',
] as const;

export const LEAGUE_SCHEDULES_PAGE_COPY = {
  title: 'Games',
  subtitle:
    'Official games imported from NeonSportz or CSV. Stream Center uses these records to match VODs to the right season, week, and matchup.',
  emptyState:
    'No games on the schedule yet. Import or sync schedule data to populate league games.',
  memberEmptyState: 'No games scheduled yet.',
  loadError: 'Could not load league games.',
  matchupColumn: 'Away @ Home',
  seasonFilterLabel: 'Season',
  weekFilterLabel: 'Week',
  teamFilterLabel: 'Team',
  searchLabel: 'Search games',
  archiveWatchLabel: 'Watch',
  archivePublishedLabel: 'Published',
  archiveLinkedLabel: 'Linked',
} as const;

export const IMPORT_SYNC_PAGE_COPY = {
  title: NAV_LABEL_IMPORT_SYNC,
  subtitle:
    'Commissioner-controlled schedule refresh from NeonSportz or CSV. Preview changes, confirm import, and apply to keep Live League Games current. This is not continuous automated sync.',
  commissionerOnlyError: 'Only league commissioners can import or sync schedules.',
} as const;

export const OVERVIEW_QUICK_ACTIONS = {
  importSync: NAV_LABEL_IMPORT_SYNC,
  leagueSchedules: 'View games',
} as const;

export function formatMatchIdentities(
  match: Pick<LscMatch, 'away_identity_value' | 'home_identity_value'>,
  archive?: LscArchiveItem | null
): string {
  const away =
    (match.away_identity_value ?? '').trim() ||
    (archive?.match_summary?.away_identity_value ?? '').trim();
  const home =
    (match.home_identity_value ?? '').trim() ||
    (archive?.match_summary?.home_identity_value ?? '').trim();

  if (away && home) {
    return `${away} / ${home}`;
  }
  if (away) {
    return away;
  }
  if (home) {
    return home;
  }
  return '—';
}

export function leagueNavLabels(isCommissioner: boolean): readonly string[] {
  return isCommissioner ? COMMISSIONER_NAV_LABELS : MEMBER_NAV_LABELS;
}

export interface LeagueNavItem {
  to: string;
  label: string;
  navKey: LscLeagueNavKey;
  end?: boolean;
  writeOnly?: boolean;
}

/** Shared league nav list for desktop sidebar and mobile drawer. */
export function buildLeagueNavItems(opts: {
  leagueBase: string;
  isCommissioner: boolean;
  showCfbNav: boolean;
  showRtts?: boolean;
  archived?: boolean;
}): LeagueNavItem[] {
  const {
    leagueBase,
    isCommissioner,
    showCfbNav,
    showRtts = false,
    archived = false,
  } = opts;

  const cfbItems: LeagueNavItem[] = showCfbNav
    ? [
        {
          to: `${leagueBase}/cfb/setup`,
          label: 'CFB Setup',
          navKey: 'cfb-setup',
          end: true,
          writeOnly: true,
        },
        {
          to: `${leagueBase}/cfb/schedule`,
          label: 'CFB Schedule',
          navKey: 'cfb-schedule',
          end: true,
        },
      ]
    : [];

  const rttsItem: LeagueNavItem | null = showRtts
    ? {
        to: `${leagueBase}/rtts`,
        label: NAV_LABEL_RTTS,
        navKey: 'rtts',
        end: true,
      }
    : null;

  const commissionerItems: LeagueNavItem[] = [
    {
      to: `${leagueBase}/command-center`,
      label: NAV_LABEL_COMMAND_CENTER,
      navKey: 'command-center',
      end: true,
    },
    { to: leagueBase, label: NAV_LABEL_HOME, navKey: 'home', end: true },
    {
      to: `${leagueBase}/setup`,
      label: NAV_LABEL_LEAGUE_SETUP,
      navKey: 'setup',
      end: true,
      writeOnly: true,
    },
    { to: `${leagueBase}/members`, label: 'Members', navKey: 'members', writeOnly: true },
    {
      to: `${leagueBase}/import`,
      label: NAV_LABEL_IMPORT_SYNC,
      navKey: 'import',
      writeOnly: true,
    },
    ...cfbItems,
    { to: `${leagueBase}/matches`, label: NAV_LABEL_LEAGUE_SCHEDULES, navKey: 'matches' },
    {
      to: `${leagueBase}/needs-attention`,
      label: 'Needs Attention',
      navKey: 'needs-attention',
      writeOnly: true,
    },
    {
      to: `${leagueBase}/vods`,
      label: 'VOD Sync',
      navKey: 'vods',
      writeOnly: true,
    },
    { to: `${leagueBase}/archive`, label: 'Archive', navKey: 'archive' },
    ...(rttsItem ? [rttsItem] : []),
    { to: `${leagueBase}/settings`, label: 'Settings', navKey: 'settings' },
  ];

  const memberItems: LeagueNavItem[] = [
    { to: leagueBase, label: NAV_LABEL_HOME, navKey: 'home', end: true },
    ...(showCfbNav
      ? [
          {
            to: `${leagueBase}/cfb/schedule`,
            label: 'CFB Schedule',
            navKey: 'cfb-schedule' as const,
            end: true,
          },
        ]
      : []),
    { to: `${leagueBase}/matches`, label: NAV_LABEL_LEAGUE_SCHEDULES, navKey: 'matches' },
    { to: `${leagueBase}/archive`, label: 'Archive', navKey: 'archive' },
    { to: `${leagueBase}/settings`, label: 'Settings', navKey: 'settings' },
  ];

  const raw = isCommissioner ? commissionerItems : memberItems;
  if (archived) {
    return raw.filter((item) => !item.writeOnly);
  }
  return raw;
}

export const ORIENTATION_UNAVAILABLE_LABEL = 'Home/away unavailable';
const UNKNOWN_SIDE_LABEL = 'Unknown';

export type AwayAtHomeFields = {
  away_side_label?: string | null;
  home_side_label?: string | null;
};

function normalizeSideLabel(label?: string | null): string | null {
  const trimmed = (label ?? '').trim();
  return trimmed || null;
}

/** Canonical imported schedule orientation: away @ home. */
export function formatAwayAtHome(match: AwayAtHomeFields): string {
  const away = normalizeSideLabel(match.away_side_label);
  const home = normalizeSideLabel(match.home_side_label);

  if (away && home) {
    return `${away} @ ${home}`;
  }
  if (away) {
    return `${away} @ ${UNKNOWN_SIDE_LABEL}`;
  }
  if (home) {
    return `${UNKNOWN_SIDE_LABEL} @ ${home}`;
  }
  return ORIENTATION_UNAVAILABLE_LABEL;
}

export function formatLeagueScheduleMatchup(
  awaySideLabel?: string | null,
  homeSideLabel?: string | null
): string {
  return formatAwayAtHome({
    away_side_label: awaySideLabel,
    home_side_label: homeSideLabel,
  });
}

/** League-scoped sidebar nav renders only when user has loaded league access. */
export function shouldRenderLeagueSidebarNav(opts: {
  leagueId?: string;
  hasLeague: boolean;
  hasError: boolean;
}): boolean {
  return Boolean(opts.leagueId && opts.hasLeague && !opts.hasError);
}

export const MOBILE_NAV_LABEL_MEMBERS = 'Members';
export const MOBILE_NAV_LABEL_SETTINGS = 'Settings';
export const MOBILE_NAV_LABEL_MANAGE = 'Manage';
export const MOBILE_NAV_LABEL_NEEDS_ATTENTION = 'Attention';
export const MOBILE_NAV_LABEL_SCHEDULE = 'Games';
export const MOBILE_NAV_LABEL_IMPORT_SCHEDULE = 'Import schedule';

export interface MobileBottomNavItem {
  to?: string;
  label: string;
  iconKey:
    | 'home'
    | 'archive'
    | 'calendar'
    | 'members'
    | 'settings'
    | 'review'
    | 'attention'
    | 'tools';
  end?: boolean;
  opensManageSheet?: boolean;
}

export function buildMobileBottomNavItems(
  leagueBase: string,
  isCommissioner: boolean,
  archived = false
): MobileBottomNavItem[] {
  if (isCommissioner) {
    const items: MobileBottomNavItem[] = [
      { to: `${leagueBase}/command-center`, label: NAV_LABEL_COMMAND_CENTER, iconKey: 'home', end: true },
      { to: `${leagueBase}/archive`, label: 'Archives', iconKey: 'archive' },
      { to: `${leagueBase}/matches`, label: MOBILE_NAV_LABEL_SCHEDULE, iconKey: 'calendar' },
      {
        to: `${leagueBase}/needs-attention`,
        label: MOBILE_NAV_LABEL_NEEDS_ATTENTION,
        iconKey: 'attention',
      },
      {
        label: MOBILE_NAV_LABEL_MANAGE,
        iconKey: 'tools',
        opensManageSheet: true,
      },
    ];
    if (archived) {
      return items.filter((item) => item.iconKey !== 'attention');
    }
    return items;
  }

  return [
    { to: leagueBase, label: NAV_LABEL_HOME, iconKey: 'home', end: true },
    { to: `${leagueBase}/archive`, label: 'Archives', iconKey: 'archive' },
    { to: `${leagueBase}/matches`, label: MOBILE_NAV_LABEL_SCHEDULE, iconKey: 'calendar' },
    { to: `${leagueBase}/settings`, label: 'Settings', iconKey: 'settings' },
  ];
}

export function shouldShowMobileBottomNav(opts: {
  leagueId?: string;
  hasLeague: boolean;
  hasError: boolean;
  loading: boolean;
}): boolean {
  return Boolean(
    opts.leagueId &&
      opts.hasLeague &&
      !opts.hasError &&
      !opts.loading
  );
}

/** Strip optional /lsc prefix so nav matching works on stream and fallback hosts. */
export function normalizeLscPathname(pathname: string): string {
  if (pathname === '/lsc') {
    return '/';
  }
  if (pathname.startsWith('/lsc/')) {
    return pathname.slice(4) || '/';
  }
  return pathname;
}

export function isLeagueHomePath(pathname: string, leagueId: string): boolean {
  const normalized = normalizeLscPathname(pathname).replace(/\/+$/, '') || '/';
  return normalized === `/leagues/${leagueId}`;
}

export function isCfbSetupPath(pathname: string, leagueId: string): boolean {
  const normalized = normalizeLscPathname(pathname).replace(/\/+$/, '') || '/';
  return normalized === `/leagues/${leagueId}/cfb/setup`;
}

export function isCfbSchedulePath(pathname: string, leagueId: string): boolean {
  const normalized = normalizeLscPathname(pathname).replace(/\/+$/, '') || '/';
  return normalized === `/leagues/${leagueId}/cfb/schedule`;
}

export type LscLeagueNavKey =
  | 'command-center'
  | 'home'
  | 'setup'
  | 'members'
  | 'import'
  | 'cfb-setup'
  | 'cfb-schedule'
  | 'matches'
  | 'needs-attention'
  | 'vods'
  | 'review'
  | 'archive'
  | 'rtts'
  | 'settings';

export function getActiveLscNavKey(pathname: string, leagueId: string): LscLeagueNavKey | null {
  const normalized = normalizeLscPathname(pathname).replace(/\/+$/, '') || '/';
  const base = `/leagues/${leagueId}`;
  if (normalized === `${base}/command-center`) {
    return 'command-center';
  }
  if (normalized === base) {
    return 'home';
  }
  if (normalized === `${base}/setup`) {
    return 'setup';
  }
  if (normalized === `${base}/cfb/setup`) {
    return 'cfb-setup';
  }
  if (normalized === `${base}/cfb/schedule`) {
    return 'cfb-schedule';
  }
  if (normalized.startsWith(`${base}/members`)) {
    return 'members';
  }
  if (normalized.startsWith(`${base}/import`)) {
    return 'import';
  }
  if (normalized.startsWith(`${base}/matches`)) {
    return 'matches';
  }
  if (normalized.startsWith(`${base}/needs-attention`)) {
    return 'needs-attention';
  }
  if (normalized.startsWith(`${base}/vods`)) {
    return 'vods';
  }
  if (normalized.startsWith(`${base}/review`)) {
    return 'review';
  }
  if (normalized.startsWith(`${base}/archive`)) {
    return 'archive';
  }
  if (normalized.startsWith(`${base}/rtts`)) {
    return 'rtts';
  }
  if (normalized.startsWith(`${base}/settings`)) {
    return 'settings';
  }
  return null;
}

export interface MobileManageItem {
  to: string;
  label: string;
  navKey: LscLeagueNavKey;
  end?: boolean;
}

export function buildMobileManageItems(leagueBase: string, archived = false): MobileManageItem[] {
  if (archived) {
    return [{ to: `${leagueBase}/settings`, label: MOBILE_NAV_LABEL_SETTINGS, navKey: 'settings' }];
  }

  return [
    {
      to: `${leagueBase}/setup`,
      label: NAV_LABEL_LEAGUE_SETUP,
      navKey: 'setup',
      end: true,
    },
    { to: `${leagueBase}/members`, label: 'Members', navKey: 'members' },
    {
      to: `${leagueBase}/import`,
      label: MOBILE_NAV_LABEL_IMPORT_SCHEDULE,
      navKey: 'import',
    },
    {
      to: `${leagueBase}/needs-attention`,
      label: 'Needs Attention',
      navKey: 'needs-attention',
    },
    { to: `${leagueBase}/settings`, label: MOBILE_NAV_LABEL_SETTINGS, navKey: 'settings' },
  ];
}

const MOBILE_MANAGE_ACTIVE_KEYS = new Set<LscLeagueNavKey>([
  'setup',
  'members',
  'import',
  'settings',
  'needs-attention',
  'cfb-setup',
]);

export function isMobileManagePathActive(pathname: string, leagueId: string): boolean {
  const activeKey = getActiveLscNavKey(pathname, leagueId);
  return activeKey !== null && MOBILE_MANAGE_ACTIVE_KEYS.has(activeKey);
}
